<template>
  <DefaultLayout>
    <div class="page-header">
      <div>
        <h1>Eventos de Integración</h1>
        <p class="subtitle">Monitorea importaciones y exportaciones REST</p>
      </div>
      <div class="actions">
        <Dropdown
          v-model="statusFilter"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Estado"
          showClear
          class="filter"
          @change="loadEvents"
        />
        <Dropdown
          v-model="typeFilter"
          :options="typeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Tipo"
          showClear
          class="filter"
          @change="loadEvents"
        />
        <Button icon="pi pi-refresh" text rounded @click="loadEvents" :loading="loading" />
      </div>
    </div>

    <Card>
      <template #content>
        <DataTable :value="events" :loading="loading" responsiveLayout="scroll">
          <Column field="created_at" header="Fecha" style="min-width: 170px">
            <template #body="{ data }">
              {{ formatDate(data.created_at) }}
            </template>
          </Column>
          <Column field="direction" header="Dirección" />
          <Column field="event_type" header="Tipo" />
          <Column field="status" header="Estado">
            <template #body="{ data }">
              <Tag :value="data.status" :severity="statusSeverity(data.status)" />
            </template>
          </Column>
          <Column field="attempt_count" header="Intentos">
            <template #body="{ data }">
              {{ data.attempt_count }} / {{ data.max_attempts }}
            </template>
          </Column>
          <Column field="response_status_code" header="HTTP" />
          <Column field="error_message" header="Error" style="min-width: 240px">
            <template #body="{ data }">
              <span class="error-text">{{ data.error_message || '—' }}</span>
            </template>
          </Column>
          <Column header="">
            <template #body="{ data }">
              <Button
                v-if="data.status === 'FAILED' || data.status === 'RETRYING'"
                label="Reintentar"
                icon="pi pi-replay"
                size="small"
                @click="retry(data.id)"
                :loading="retryingId === data.id"
              />
            </template>
          </Column>
          <template #empty>
            <p class="empty-msg">No hay eventos de integración para los filtros actuales.</p>
          </template>
        </DataTable>
      </template>
    </Card>
  </DefaultLayout>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import DefaultLayout from '../components/layout/DefaultLayout.vue';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import { integrationsService } from '../services/integrations';

const toast = useToast();
const loading = ref(false);
const retryingId = ref(null);
const events = ref([]);
const statusFilter = ref(null);
const typeFilter = ref(null);

const statusOptions = [
  { label: 'PENDING', value: 'PENDING' },
  { label: 'PROCESSING', value: 'PROCESSING' },
  { label: 'SUCCESS', value: 'SUCCESS' },
  { label: 'FAILED', value: 'FAILED' },
  { label: 'RETRYING', value: 'RETRYING' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

const typeOptions = [
  { label: 'document_import', value: 'document_import' },
  { label: 'result_export', value: 'result_export' },
];

const statusSeverity = (status) => ({
  PENDING: 'info',
  PROCESSING: 'warning',
  SUCCESS: 'success',
  FAILED: 'danger',
  RETRYING: 'warning',
  CANCELLED: 'secondary',
}[status] || 'secondary');

const formatDate = (value) => value ? new Date(value).toLocaleString('es-CO') : '—';

const loadEvents = async () => {
  loading.value = true;
  try {
    const data = await integrationsService.listEvents({
      status_filter: statusFilter.value || undefined,
      event_type: typeFilter.value || undefined,
      limit: 100,
    });
    events.value = data.items || [];
  } catch (error) {
    const detail = error?.response?.data?.detail?.message || error?.response?.data?.detail || 'No se pudo cargar la lista.';
    toast.add({ severity: 'error', summary: 'Error', detail, life: 5000 });
  } finally {
    loading.value = false;
  }
};

const retry = async (eventId) => {
  retryingId.value = eventId;
  try {
    await integrationsService.retryEvent(eventId);
    toast.add({ severity: 'success', summary: 'Reintento enviado', detail: `Evento ${eventId} reprocesado.`, life: 3500 });
    await loadEvents();
  } catch (error) {
    const detail = error?.response?.data?.detail?.message || error?.response?.data?.detail || 'No se pudo reintentar el evento.';
    toast.add({ severity: 'error', summary: 'Error', detail, life: 5000 });
  } finally {
    retryingId.value = null;
  }
};

onMounted(loadEvents);
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
}
.subtitle { color: #6b7280; margin-top: 0.25rem; }
.actions { display: flex; align-items: center; gap: 0.5rem; }
.filter { min-width: 170px; }
.error-text { color: #b91c1c; }
.empty-msg { color: #6b7280; margin: 0; }
</style>
